import type { AutopilotAgent, EnableAutopilotResponse, Server } from "@cloudserver/contracts";
import { MonitorPlay } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnableAutopilot } from "../../../api/autopilot.js";
import { ErrorMessage } from "../../../ui/feedback.js";
import { Dialog } from "../../../ui/overlay.js";
import { Button } from "../../../ui/primitives.js";
import { SendToSession } from "../../../terminal/send.js";
import { CommandBlock } from "./command.js";
import { DockerOption } from "./docker-option.js";
import { RoleOptions } from "./role-options.js";

/**
 * Turning Autopilot on for a running server, or a fresh install command
 * (after a rebuild): the choice of container monitoring, then the one-line
 * command with its one-time token — to copy, or to type in the server's
 * console. The token lives only in this dialog: closing it forgets it.
 */
export function EnableDialog({
  projectId,
  server,
  agent,
  open,
  onOpenChange,
}: {
  projectId: string;
  server: Server;
  agent: AutopilotAgent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const enable = useEnableAutopilot(projectId, server.id);
  const [containers, setContainers] = useState(agent?.containers ?? true);
  const [role, setRole] = useState(agent?.role ?? "monitoring");
  const [answer, setAnswer] = useState<EnableAutopilotResponse | null>(null);
  const running = server.status === "running";

  const close = (next: boolean) => {
    if (!next) {
      setAnswer(null);
      enable.reset();
    }
    onOpenChange(next);
  };
  const until = answer
    ? new Intl.DateTimeFormat(i18n.language, { hour: "2-digit", minute: "2-digit" }).format(
        new Date(answer.expiresAt),
      )
    : "";

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={answer ? t("autopilot.enable.commandTitle") : t("autopilot.enable.title")}
      description={
        answer
          ? t("autopilot.enable.commandLead", { time: until })
          : agent
            ? t("autopilot.enable.reinstall")
            : t("autopilot.enable.lead")
      }
      footer={
        answer ? (
          <>
            {running ? (
              <SendToSession
                projectId={projectId}
                server={server}
                text={answer.command}
                open="console"
                label={t("autopilot.enable.typeInConsole")}
                onDone={() => close(false)}
              />
            ) : (
              <Button
                variant="secondary"
                icon={<MonitorPlay className="size-4" aria-hidden />}
                disabled
                title={t("autopilot.enable.consoleOff")}
              >
                {t("autopilot.enable.typeInConsole")}
              </Button>
            )}
            <Button onClick={() => close(false)}>{t("autopilot.enable.done")}</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => close(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              busy={enable.isPending}
              onClick={() => enable.mutate({ containers, role }, { onSuccess: setAnswer })}
            >
              {t("autopilot.enable.submit")}
            </Button>
          </>
        )
      }
    >
      {answer ? (
        <div className="flex flex-col gap-3 text-sm">
          <CommandBlock label={t("autopilot.enable.commandLabel")} command={answer.command} />
          <p className="text-muted">
            {running ? t("autopilot.enable.consoleHint") : t("autopilot.enable.consoleOff")}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 text-sm">
          <ul className="flex list-disc flex-col gap-1 pl-5 text-muted">
            <li>{t("autopilot.what.metrics")}</li>
            <li>{t("autopilot.what.services")}</li>
            <li>{t("autopilot.what.safety")}</li>
          </ul>
          <DockerOption checked={containers} onChange={setContainers} />
          <RoleOptions legend={t("autopilot.role.title")} value={role} onChange={setRole} />
          <ErrorMessage error={enable.error} />
        </div>
      )}
    </Dialog>
  );
}
